/**
 * App boot: loads the generated data, initialises the stores (time range, deep link) and creates the scene.
 */
import type { BodyTable } from './core/bodyEphemeris';
import type { AgencyData, BodyData, MissionData, MissionsFile, TrajectoryData } from './core/data';
import { parseEnd } from './core/missionState';
import { type LoaderSinks, Loader } from './state/loader';
import { OVERVIEW } from './core/scaleProfiles';
import { World } from './scene/world';
import { isMoment } from './core/moments';
import { startTier } from './core/quality';
import { parseUtc, tdbDaysToUtcMs } from './core/time';
import { type Lang, lang, setLang } from './i18n';
import { parseDeepLink, type DeepLink } from './state/deepLink';
import { introSeen, overrideQuality } from './state/settings';
import {
  SPEEDS,
  currentDate,
  flybyEvents,
  registerSpacecraft,
  selectMission,
  focus,
  focusOn,
  follow,
  followTarget,
  playback,
  scaleMode,
  setDate,
  timeRange,
} from './state/stores';
import { ZeitflugScene, yieldToMain } from './scene/Scene';
import { get } from 'svelte/store';

export interface FocusTarget {
  id: string;
  name: { en: string; de: string };
  kind: 'body' | 'spacecraft';
  /** Parent body for moons (breadcrumb). */
  parent?: string;
  /** Bodies: their kind (planet, moon, kbo, …; timeline grouping by destination). */
  bodyKind?: string;
  /** Spacecraft: their mission and launch (or separation), UTC ms. */
  mission?: string;
  launch?: number;
}

export interface TimelineEvent {
  time: number;
  title: { en: string; de: string };
  type: string;
  spacecraft?: string;
  body?: string;
  /** A moment (S4-13: flybys, landings; burns of crewed missions): title card, slow-motion, cinematic framing. */
  moment?: boolean;
  /** The event belongs to a crewed mission (shorter title-card window, S4-13). */
  crewed?: boolean;
}

/** A mission as the timeline, the detail panel and the list show it (S3-15/S3-16). */
export interface MissionInfo {
  id: string;
  data: MissionData;
  /** First agency's colour (D17). */
  color: string;
  agencies: AgencyData[];
  /** Icon type of its main spacecraft (the first one). */
  type: string;
  /** Launch (UTC ms). */
  launch: number;
  /** End of the mission (contact lost / plunge), UTC ms, or null while active. */
  end: number | null;
  /** End of its (predicted) trajectory, UTC ms, or null without one. */
  trajectoryEnd: number | null;
  spacecraft: string[];
  /** Its events in chronological order. */
  events: TimelineEvent[];
}

export interface AppContext {
  scene: ZeitflugScene;
  /** All missions, by launch (S3-15). */
  missions: MissionInfo[];
  /** The real "now" (UTC ms): later parts of trajectories are predictions (dashed / hatched). */
  now: number;
  /** Mission events in chronological order (toasts while playing, S1-22). */
  events: TimelineEvent[];
  targets: Map<string, FocusTarget>;
  deepLink: DeepLink;
  /** Play the intro sequence on start (S2-12). */
  intro: boolean;
  /** The date the app opened at (UTC ms) and the spacecraft in flight then, for the intro title (S4-25). */
  start: { time: number; inFlight: string[] };
}

async function loadJson<T>(path: string): Promise<T> {
  const res = await fetch(`${import.meta.env.BASE_URL}data/${path}`);
  if (!res.ok) throw new Error(`${path}: HTTP ${res.status}`);
  return (await res.json()) as T;
}

export class WebGLUnavailableError extends Error {}

/**
 * Whether the browser has WebGL 2 at all. Whether it can create a context shows when the scene creates its renderer
 * (see boot): a throwaway context here cost 166–184 ms at 4× CPU, the longest task of the start (S4-03).
 */
function webglAvailable(): boolean {
  return typeof WebGL2RenderingContext !== 'undefined';
}

/**
 * Loads the data, sets up the stores and creates the scene. `onProgress` receives 0…1 for the loading screen
 * (S2-12): data files, trajectories, scene setup, shader compilation.
 */
/** Intro: at most 3.5 s dark hold + 4.8 s sweep (Scene), then the remaining trajectories load. */
const INTRO_LOADER_DELAY_MS = 8500;
/**
 * Without the intro the remaining trajectories (Cassini alone is 274 kB) wait until the start is over (S4-03, D14 step
 * 3). Not longer: at 8 s, chosen for Lighthouse's five quiet seconds, the maintainer found the loading after the start
 * too slow (S4-15). What the visitor asks for (focus, follow) still loads at once.
 */
const LOADER_DELAY_MS = 3000;

export async function boot(
  canvas: HTMLCanvasElement,
  labelLayer: HTMLElement,
  onProgress: (fraction: number) => void = () => {},
): Promise<AppContext> {
  const deepLink = parseDeepLink(window.location.search);
  if (deepLink.lang) setLang(deepLink.lang);
  else setLang(get(lang) as Lang);
  if (!webglAvailable()) throw new WebGLUnavailableError('WebGL 2 is not available');

  onProgress(0.1);
  const [missions, bodies] = await Promise.all([
    loadJson<MissionsFile>('missions.json'),
    loadJson<BodyData[]>('bodies.json'),
  ]);
  onProgress(0.3);

  // the range starts at the earliest launch (D12): 1957-10-04 with Sputnik 1 (Tier B, S3-21)
  const starts = missions.missions.flatMap((m) => [
    parseUtc(m.events.find((e) => e.type === 'launch')?.date ?? m.launch.date),
    ...m.spacecraft.flatMap((sc) => (sc.trajectory.coverage ? [tdbDaysToUtcMs(sc.trajectory.coverage[0])] : [])),
  ]);
  const min = Math.ceil(Math.min(...starts) / 60_000) * 60_000;
  // the range reaches into the predicted part of active missions' trajectories (Concept §2.3, at most two years)
  const now = Date.now();
  // the app opens at today, paused (S4-25, D19): what still flies first, the past one scrub away
  const start = Math.max(min, Math.floor(now / 60_000) * 60_000);
  const predictedEnds = missions.missions
    .filter((m) => m.status === 'active')
    .flatMap((m) =>
      m.spacecraft.flatMap((sc) => (sc.trajectory.coverage ? [tdbDaysToUtcMs(sc.trajectory.coverage[1])] : [])),
    );
  const max = Math.min(now + 2 * 365.25 * 86_400_000, Math.max(now, ...predictedEnds));
  timeRange.set({ min, max });
  currentDate.set(start);

  const targets = new Map<string, FocusTarget>();
  for (const b of bodies) {
    const target: FocusTarget = { id: b.id, name: b.name, kind: 'body', bodyKind: b.kind };
    if (b.parent && b.parent !== 'sun') target.parent = b.parent;
    targets.set(b.id, target);
  }
  for (const m of missions.missions) {
    for (const sc of m.spacecraft) {
      const name = sc.name ?? m.name.en;
      const launch = sc.trajectory.coverage ? tdbDaysToUtcMs(sc.trajectory.coverage[0]) : parseUtc(m.launch.date);
      targets.set(sc.id, { id: sc.id, name: { en: name, de: name }, kind: 'spacecraft', mission: m.id, launch });
      registerSpacecraft(sc.id, m.id);
    }
  }
  const crewedMission = (m: MissionData): boolean => m.spacecraft.some((sc) => sc.type === 'crewed');
  const events: TimelineEvent[] = missions.missions
    .flatMap((m) =>
      m.events.map((e): TimelineEvent => ({
        time: parseUtc(e.date),
        title: e.title,
        type: e.type,
        ...(e.spacecraft ? { spacecraft: e.spacecraft } : m.spacecraft[0] ? { spacecraft: m.spacecraft[0].id } : {}),
        ...(e.body ? { body: e.body } : {}),
        ...(e.body && isMoment(e.type, crewedMission(m)) ? { moment: true } : {}),
        ...(crewedMission(m) ? { crewed: true } : {}),
      })),
    )
    .sort((a, b) => a.time - b.time);

  flybyEvents.set(
    missions.missions.flatMap((m) =>
      m.events
        .filter((e) => e.body && isMoment(e.type, crewedMission(m)))
        .map((e) => ({
          time: parseUtc(e.date),
          spacecraft: e.spacecraft ?? m.spacecraft[0]!.id,
          mission: m.id,
          body: e.body!,
        })),
    ),
  );

  // Deep link state (S1-19), applied before the scene subscribes so it takes effect without animation
  if (deepLink.t !== undefined) setDate(deepLink.t);
  if (deepLink.mode) scaleMode.set(deepLink.mode);
  const speed = SPEEDS.find((s) => s.id === deepLink.speed);
  if (speed) playback.update((p) => ({ ...p, speed: speed.value }));
  if (deepLink.follow && targets.get(deepLink.follow)?.kind === 'spacecraft') follow(deepLink.follow);
  else if (deepLink.focus && targets.has(deepLink.focus)) focusOn(deepLink.focus);
  // ?mission= opens the panel (D16); without focus/follow the camera goes to the mission's spacecraft in flight then
  const linked = deepLink.mission ? missions.missions.find((m) => m.id === deepLink.mission) : undefined;
  if (linked) {
    const t = get(currentDate);
    const inFlight = linked.spacecraft.find(
      (sc) =>
        sc.trajectory.coverage &&
        t >= tdbDaysToUtcMs(sc.trajectory.coverage[0]) &&
        t <= tdbDaysToUtcMs(sc.trajectory.coverage[1]),
    );
    if (inFlight && !deepLink.follow && !deepLink.focus) focusOn(inFlight.id);
    selectMission(linked.id);
  }
  if (deepLink.play) playback.update((p) => ({ ...p, playing: true }));
  if (deepLink.quality) overrideQuality(deepLink.quality);

  // Lazy loading (S3-03): before the first frame only the trajectories active at the start date (and the tables they
  // require); the rest follows one file at a time in idle time, the focused or followed spacecraft first.
  const loader = new Loader(missions, bodies, `${import.meta.env.BASE_URL}data/`, () => get(currentDate));
  const trajectories = new Map<string, TrajectoryData>();
  const lineTimes = new Map<string, Float64Array>();
  const tables = new Map<string, BodyTable>();
  const atStart = loader.activeAt(get(currentDate));
  const wanted = [get(followTarget), get(focus)].filter((id): id is string => !!id && loader.status(id) !== undefined);
  // the scene does not exist yet: collect, then hand everything over at once
  const early: LoaderSinks = {
    trajectory: (id, data, times) => {
      trajectories.set(id, data);
      lineTimes.set(id, times);
    },
    table: (id, table) => tables.set(id, table),
    bands: () => bandsBeforeScene(bodies),
  };
  await loader.load([...atStart, ...wanted], early);
  onProgress(0.7);
  // from here the start-up runs in steps with task boundaries between them (S4-03): no long task before the first frame
  await yieldToMain();
  performance.mark('zeitflug:data-loaded');
  // a phone (touch, short side under 500 CSS px) starts the automatic quality one tier lower (S4-15)
  const phone =
    !!globalThis.matchMedia?.('(pointer: coarse)').matches && Math.min(window.screen.width, window.screen.height) < 500;
  let scene: ZeitflugScene;
  try {
    scene = new ZeitflugScene({
      canvas,
      labelLayer,
      data: { bodies, missions, trajectories, tables, lineTimes },
      startTier: startTier(phone),
      ...(phone ? { sampleBudgetMs: 4 } : {}),
    });
  } catch (err) {
    // no context (blocklisted GPU, GPU process gone): the same as no WebGL 2 at all; anything else stays an error
    if (!canvas.getContext('webgl2')) throw new WebGLUnavailableError('WebGL 2 is not available');
    throw err;
  }
  performance.measure('zeitflug:scene-construct', 'zeitflug:data-loaded');
  performance.mark('zeitflug:populate');
  await scene.populate();
  performance.measure('zeitflug:scene-populate', 'zeitflug:populate');
  performance.measure('zeitflug:scene-setup', 'zeitflug:data-loaded');
  const sinks: LoaderSinks = {
    trajectory: (id, data, times) => scene.addTrajectory(id, data, times),
    table: (id, table) => scene.addBodyTable(id, table),
    bands: () => scene.lineBands(),
  };
  // whatever the user looks at loads first
  const requestFor = (id: string | null): void => {
    if (id) loader.request(id);
  };
  focus.subscribe(requestFor);
  followTarget.subscribe(requestFor);

  // Handle for Playwright tests and console debugging.
  (window as unknown as { __zeitflug: ZeitflugScene }).__zeitflug = scene;
  if (deepLink.debug) {
    const { createDebugPanel } = await import('./ui/debugPanel');
    createDebugPanel(scene);
  }
  onProgress(0.85);
  await yieldToMain();
  performance.mark('zeitflug:precompile');
  await scene.precompile();
  performance.measure('zeitflug:precompile', 'zeitflug:precompile');
  onProgress(1);
  // the scene starts once the HUD is in (App, S4-03 part 6): its first framing fits the view the HUD leaves free
  scene.loader = loader;
  // the intro plays on a first visit without deep-link state; ?intro=1/0 forces it either way
  const stateLink = [
    deepLink.t,
    deepLink.mode,
    deepLink.focus,
    deepLink.follow,
    deepLink.speed,
    deepLink.play,
    deepLink.mission,
  ].some((v) => v !== undefined);
  const intro = deepLink.intro ?? (!stateLink && !introSeen());
  // background loading waits until after the intro: decoding and sampling trails would cost the sweep its frames
  loader.start(sinks, intro ? INTRO_LOADER_DELAY_MS : LOADER_DELAY_MS);
  const opened = get(currentDate);
  // in flight at the opening date: the trajectory covers it and the mission has not ended (silent Pioneers do not count)
  const inFlight = missions.missions.flatMap((m) =>
    m.spacecraft
      .filter((sc) => {
        const c = sc.trajectory.coverage;
        const end = parseEnd(m.end);
        return (
          !!c && opened >= tdbDaysToUtcMs(c[0]) && opened <= tdbDaysToUtcMs(c[1]) && (end === null || opened < end)
        );
      })
      .map((sc) => sc.id),
  );
  // in launch order, like the timeline
  inFlight.sort((a, b) => (targets.get(a)?.launch ?? 0) - (targets.get(b)?.launch ?? 0));
  const agencyById = new Map(missions.agencies.map((a) => [a.id, a]));
  const missionInfos: MissionInfo[] = missions.missions
    .map((m): MissionInfo => {
      const coverageEnds = m.spacecraft.flatMap((sc) =>
        sc.trajectory.coverage ? [tdbDaysToUtcMs(sc.trajectory.coverage[1])] : [],
      );
      const launchEvent = m.events.find((e) => e.type === 'launch');
      return {
        id: m.id,
        data: m,
        color: agencyById.get(m.agencies[0] ?? '')?.color ?? '#ffffff',
        agencies: m.agencies.flatMap((a) => (agencyById.has(a) ? [agencyById.get(a)!] : [])),
        type: m.spacecraft[0]?.type ?? 'flyby',
        launch: parseUtc(launchEvent?.date ?? m.launch.date),
        end: parseEnd(m.end),
        trajectoryEnd: coverageEnds.length ? Math.max(...coverageEnds) : null,
        spacecraft: m.spacecraft.map((sc) => sc.id),
        events: events.filter((e) => m.spacecraft.some((sc) => sc.id === e.spacecraft)),
      };
    })
    .sort((a, b) => a.launch - b.launch);
  return { scene, missions: missionInfos, now, events, targets, deepLink, intro, start: { time: opened, inFlight } };
}

/** Line bands before the scene exists (a throw-away world with the same bodies; S3-03). */
function bandsBeforeScene(bodies: BodyData[]): Record<string, ReturnType<World['moonBands']>[number][]> {
  const w = new World(bodies, { ...OVERVIEW });
  const out: Record<string, ReturnType<World['moonBands']>[number][]> = { sun: [...w.moonBands('sun')] };
  for (const b of w.planets) {
    const bands = w.moonBands(b.data.id);
    if (bands.length) out[b.data.id] = [...bands];
  }
  return out;
}
